import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { withTranslation } from "react-i18next";
import { Button, Grid, TextField, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import SelectField from "Components/Common/SelectField";
import PowerAbstractTable from "./PowerAbstractTable";
import { getOptionValue, tariff } from "common/enums";

import { useLazyDownloadExcelQuery } from "slices/api";
import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";

const initialFilters = {
  consumerNumber: "",
  tariffType: "",
};

const PowerAbstract = ({ t }) => {
  const [billCycleId, setBillCycleId] = useState("");
  const [query, setQuery] = useState({});
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [searchedBillCycledId, setSearchedBillCycledId] = useState("");

  const [{ consumerNumber, tariffType }, setSelectedFilteredOption] =
    useState(initialFilters);

  const resetPageRef = useRef();

  const sm = useMediaQuery("(max-width: 599px)");

  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const downloadPowerAbstractReport = async () => {
    try {
      if (searchedBillCycledId) {
        await downloadExcel({
          endpoint: `/report/billing-cycle/${searchedBillCycledId}/electricity-bill-abstract-report-excel`,
          sheetName: "Power Abstract",
          query,
        });
      }
    } catch (error) {}
  };

  const powerAbstractTableProps = useMemo(
    () => ({
      billingCycleId: searchedBillCycledId,
      query,
    }),
    [searchedBillCycledId, query]
  );

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType }) =>
                subscriptionType ===
                getOptionValue("subscriptionTypes", "Electricity")
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions]
  );

  const tariffOptions = useMemo(
    () => [{ value: "", label: "All" }, ...tariff],
    [tariff]
  );

  useEffect(() => {
    if (updatedBillingCycleOptions.length) {
      const billingCycleId =
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "";
      setBillCycleId(billingCycleId);
      setSearchedBillCycledId(billingCycleId);
    }
  }, [updatedBillingCycleOptions]);

  const clearBtnHandler = () => {
    setSelectedFilteredOption(initialFilters);
    setQuery({});
    handleResetPage();
  };

  const searchBtnHandler = (e) => {
    e.preventDefault();
    setSearchedBillCycledId(billCycleId);
    setQuery({
      consumerNumber,
      tariff: tariffType,
    });
    handleResetPage();
  };

  return (
    <>
      <Backdrop open={isExcelFileDownloading} />
      <BreadCrumb title={t("Power Abstract")} pageTitle={t("Billing")} />
      <form onSubmit={searchBtnHandler}>
        <Grid container columnSpacing={2} rowSpacing={1}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              onChange={(_, { value }) => {
                setBillCycleId(value);
              }}
              value={
                updatedBillingCycleOptions.find(
                  ({ value }) => value === billCycleId
                ) ?? ""
              }
              options={updatedBillingCycleOptions}
              renderInput={(params) => {
                delete params.inputProps.id;
                delete params.id;
                return <TextField {...params} label={t("Select bill cycle")} />;
              }}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              type="number"
              value={consumerNumber}
              onChange={(e) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  consumerNumber: e.target.value,
                }))
              }
              placeholder={t("Enter consumer number")}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Tariff")}
              onChange={(_, { value }) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  tariffType: value,
                }))
              }
              options={tariffOptions}
              value={
                tariffOptions.find(({ value }) => value === tariffType) ?? ""
              }
            />
          </Grid>

          <Grid item lg={3}>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              onClick={clearBtnHandler}
            >
              {t("Clear")}
            </Button>
            <Button
              variant="contained"
              className="mb-2 me-2"
              onClick={downloadPowerAbstractReport}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <PowerAbstractTable
        powerAbstractTableProps={powerAbstractTableProps}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 250px)"}
      />
    </>
  );
};

PowerAbstract.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(PowerAbstract);
